<template>
  <div class="customers-map-container">
    <div class="map-header">
      <h5 class="map-title">
        <i class="bi bi-geo-alt-fill me-2"></i>
        Localisation des Clients
      </h5>
      <span class="map-badge">
        {{ allCustomersWithLocation.length }} Clients
        <span class="badge-separator">|</span>
        {{ customersWithLocation.length }} Clients
        <span class="badge-separator">|</span>
        {{ sellerCustomersWithLocation.length }} Clients Vendeurs
      </span>
    </div>
    <div id="customers-map" class="customers-map"></div>
  </div>
</template>

<script>
export default {
  name: "CustomersMap",
  data() {
    return {
      map: null,
      markers: [],
      customers: [],
      sellerCustomers: [],
      loading: false,
    };
  },
  computed: {
    customersWithLocation() {
      if (!this.customers || !Array.isArray(this.customers)) {
        return [];
      }
      return this.customers.filter(
        (customer) =>
          customer.latitude &&
          customer.longitude &&
          !isNaN(parseFloat(customer.latitude)) &&
          !isNaN(parseFloat(customer.longitude))
      );
    },
    sellerCustomersWithLocation() {
      if (!this.sellerCustomers || !Array.isArray(this.sellerCustomers)) {
        return [];
      }
      // Return all seller customers, use default location if no coordinates
      return this.sellerCustomers.map((sellerCustomer) => {
        const hasValidCoords =
          sellerCustomer.latitude &&
          sellerCustomer.longitude &&
          !isNaN(parseFloat(sellerCustomer.latitude)) &&
          !isNaN(parseFloat(sellerCustomer.longitude));

        return {
          ...sellerCustomer,
          latitude: hasValidCoords ? sellerCustomer.latitude : 34.6867, // Default to Oujda
          longitude: hasValidCoords ? sellerCustomer.longitude : -1.9086,
          hasCoordinates: hasValidCoords,
        };
      });
    },
    allCustomersWithLocation() {
      return (
        this.customersWithLocation.length +
        this.sellerCustomersWithLocation.length
      );
    },
  },
  watch: {
    customersWithLocation: {
      handler() {
        this.updateMap();
      },
      deep: true,
    },
    sellerCustomersWithLocation: {
      handler() {
        this.updateMap();
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchCustomers();
    await this.fetchSellerCustomers();
    this.loadLeaflet();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    async fetchCustomers() {
      try {
        this.loading = true;
        const data = await this.$api.fetch("customers");
        this.customers = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching customers:", error);
        this.customers = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchSellerCustomers() {
      try {
        this.loading = true;
        const data = await this.$api.fetch("sellers_customers");
        this.sellerCustomers = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching seller customers:", error);
        this.sellerCustomers = [];
      } finally {
        this.loading = false;
      }
    },
    loadLeaflet() {
      // Check if Leaflet is already loaded
      if (window.L) {
        this.initMap();
        return;
      }

      // Load Leaflet CSS
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
      link.crossOrigin = "";
      document.head.appendChild(link);

      // Load Leaflet JS
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.integrity = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";
      script.crossOrigin = "";
      script.onload = () => {
        this.initMap();
      };
      document.body.appendChild(script);
    },
    initMap() {
      this.$nextTick(() => {
        if (!window.L) {
          console.error("Leaflet not loaded");
          return;
        }

        // Initialize map - Oriental Morocco (Oujda region)
        // Define bounds for Oriental Morocco region
        const orientalMoroccoBounds = [
          [33.5, -3.5], // Southwest corner
          [35.5, -0.5], // Northeast corner
        ];

        this.map = window.L.map("customers-map", {
          center: [34.6867, -1.9086],
          zoom: 8,
          maxBounds: orientalMoroccoBounds,
          maxBoundsViscosity: 1.0, // Prevent panning outside bounds
          minZoom: 7,
          maxZoom: 15,
        });

        // Add tile layer
        window.L.tileLayer(
          "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 15,
            minZoom: 7,
          }
        ).addTo(this.map);

        // Set initial view to Oriental Morocco
        this.map.setView([34.6867, -1.9086], 8);

        // Add markers for customers
        this.updateMap();
      });
    },
    updateMap() {
      if (!this.map || !window.L) {
        return;
      }

      // Clear existing markers
      this.markers.forEach((marker) => {
        this.map.removeLayer(marker);
      });
      this.markers = [];

      // Always show seller customers, even if no regular customers
      if (
        this.sellerCustomersWithLocation.length === 0 &&
        this.customersWithLocation.length === 0
      ) {
        return;
      }

      // Create markers for each customer
      const bounds = [];
      this.customersWithLocation.forEach((customer) => {
        const lat = parseFloat(customer.latitude);
        const lng = parseFloat(customer.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          return;
        }

        // Create custom icon for regular customers (blue)
        const customIcon = window.L.divIcon({
          className: "custom-marker",
          html: `<div class="marker-icon marker-icon-customer">
            <i class="bi bi-geo-alt-fill"></i>
          </div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 30],
        });

        // Create marker
        const marker = window.L.marker([lat, lng], { icon: customIcon }).addTo(
          this.map
        );

        // Create popup content
        const popupContent = `
          <div class="customer-popup">
            <div class="popup-badge popup-badge-customer">Client</div>
            <h6 class="mb-2">${
              customer.fullName ||
              customer.firstName + " " + (customer.lastName || "")
            }</h6>
            ${
              customer.phone
                ? `<p class="mb-1"><i class="bi bi-telephone me-1"></i>${customer.phone}</p>`
                : ""
            }
            ${
              customer.city
                ? `<p class="mb-1"><i class="bi bi-geo-alt me-1"></i>${customer.city}</p>`
                : ""
            }
            ${
              customer.address
                ? `<p class="mb-0 text-muted small">${customer.address}</p>`
                : ""
            }
          </div>
        `;

        marker.bindPopup(popupContent);
        this.markers.push(marker);
        bounds.push([lat, lng]);
      });

      // Seller customers - green markers (show all, even without coordinates)
      this.sellerCustomersWithLocation.forEach((sellerCustomer) => {
        const lat = parseFloat(sellerCustomer.latitude);
        const lng = parseFloat(sellerCustomer.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          return;
        }

        // Create custom icon for seller customers (green)
        // Use different opacity for customers without coordinates
        const iconClass = sellerCustomer.hasCoordinates
          ? "marker-icon-seller-customer"
          : "marker-icon-seller-customer marker-icon-no-coords";

        const customIcon = window.L.divIcon({
          className: "custom-marker",
          html: `<div class="marker-icon ${iconClass}">
            <i class="bi bi-geo-alt-fill"></i>
          </div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 30],
        });

        // Create marker
        const marker = window.L.marker([lat, lng], { icon: customIcon }).addTo(
          this.map
        );

        // Create popup content
        const locationNote = sellerCustomer.hasCoordinates
          ? ""
          : '<p class="mb-1 text-warning small"><i class="bi bi-exclamation-triangle me-1"></i>Localisation par défaut</p>';

        const popupContent = `
          <div class="customer-popup">
            <div class="popup-badge popup-badge-seller">Client Vendeur</div>
            <h6 class="mb-2">${
              sellerCustomer.fullName ||
              sellerCustomer.firstName + " " + (sellerCustomer.lastName || "")
            }</h6>
            ${locationNote}
            ${
              sellerCustomer.phone
                ? `<p class="mb-1"><i class="bi bi-telephone me-1"></i>${sellerCustomer.phone}</p>`
                : ""
            }
            ${
              sellerCustomer.email
                ? `<p class="mb-1"><i class="bi bi-envelope me-1"></i>${sellerCustomer.email}</p>`
                : ""
            }
            ${
              sellerCustomer.seller_reference
                ? `<p class="mb-0 text-muted small"><i class="bi bi-person-badge me-1"></i>Vendeur: ${sellerCustomer.seller_reference}</p>`
                : ""
            }
          </div>
        `;

        marker.bindPopup(popupContent);
        this.markers.push(marker);
        // Only add to bounds if it has valid coordinates, or if it's the only type
        if (
          sellerCustomer.hasCoordinates ||
          this.customersWithLocation.length === 0
        ) {
          bounds.push([lat, lng]);
        }
      });

      // Fit map to show all markers with valid coordinates
      // But restrict to Oriental Morocco bounds
      const orientalMoroccoBounds = [
        [33.5, -3.5], // Southwest corner
        [35.5, -0.5], // Northeast corner
      ];

      if (bounds.length > 0) {
        // Filter bounds to only include markers within Oriental Morocco
        const validBounds = bounds.filter(([lat, lng]) => {
          return lat >= 33.5 && lat <= 35.5 && lng >= -3.5 && lng <= -0.5;
        });

        if (validBounds.length > 0) {
          this.map.fitBounds(validBounds, {
            padding: [50, 50],
            maxBounds: orientalMoroccoBounds,
          });
        } else {
          // If no valid markers in Oriental Morocco, show default view
          this.map.setView([34.6867, -1.9086], 8);
        }
      } else {
        // If no markers with coordinates, keep default view (Oriental Morocco)
        this.map.setView([34.6867, -1.9086], 8);
      }
    },
  },
};
</script>

<style scoped>
.customers-map-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(4, 108, 193, 0.08);
  overflow: hidden;
  margin-bottom: 2rem;
}

.map-header {
  background: linear-gradient(135deg, #046cc1 0%, #1588d9 100%);
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.map-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
}

.map-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badge-separator {
  opacity: 0.6;
  margin: 0 0.25rem;
}

.customers-map {
  width: 100%;
  height: 400px;
  z-index: 1;
}

/* Custom marker styles */
::v-deep .custom-marker {
  background: transparent;
  border: none;
}

::v-deep .marker-icon {
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
}

::v-deep .marker-icon-customer {
  background: #046cc1;
}

::v-deep .marker-icon-seller-customer {
  background: #28a745;
}

::v-deep .marker-icon-no-coords {
  opacity: 0.6;
  border-style: dashed;
}

::v-deep .marker-icon i {
  color: white;
  font-size: 14px;
  transform: rotate(45deg);
}

/* Popup styles */
::v-deep .customer-popup {
  min-width: 200px;
}

::v-deep .popup-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: white;
}

::v-deep .popup-badge-customer {
  background: #046cc1;
}

::v-deep .popup-badge-seller {
  background: #28a745;
}

::v-deep .customer-popup h6 {
  color: #046cc1;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

::v-deep .customer-popup p {
  margin: 0;
  font-size: 0.875rem;
}

::v-deep .leaflet-popup-content-wrapper {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

::v-deep .leaflet-popup-tip {
  background: white;
}

@media (max-width: 768px) {
  .customers-map {
    height: 400px;
  }

  .map-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
